/** Reading a channel at a distance that is not one of its samples.
 *
 * Every chart, delta and report in the analysis rests on these three: a
 * binary search that is Python's `bisect_left` to the letter (it is also run
 * over axes that are NOT sorted — a lap whose distance ran backwards through
 * a rewind — and must then land on the same index the logger landed on),
 * linear interpolation clamped at the ends, and the distance grid every
 * series is resampled onto.
 */

import { roundTo } from './round';

/** A read that cannot silently become `undefined` in arithmetic. */
export function at(arr: ArrayLike<number>, i: number): number {
  const v = arr[i];
  if (v === undefined) throw new RangeError(`index ${i} is outside a channel of ${arr.length}`);
  return v;
}

/** Leftmost insertion point for `x` in `xs[lo:hi]`, as Python's `bisect_left`. */
export function bisectLeft(xs: ArrayLike<number>, x: number, lo = 0, hi = xs.length): number {
  while (lo < hi) {
    const mid = Math.floor((lo + hi) / 2);
    if (xs[mid]! < x) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

/** Rightmost insertion point for `x` in `xs[lo:hi]`, as Python's `bisect_right`. */
export function bisectRight(xs: ArrayLike<number>, x: number, lo = 0, hi = xs.length): number {
  while (lo < hi) {
    const mid = Math.floor((lo + hi) / 2);
    if (x < xs[mid]!) hi = mid;
    else lo = mid + 1;
  }
  return lo;
}

/** Linear interpolation with edge clamping. xs must be ascending. */
export function interp(xs: ArrayLike<number>, ys: ArrayLike<number>, x: number): number {
  const n = xs.length;
  if (n === 0) return 0;
  if (x <= xs[0]!) return at(ys, 0);
  if (x >= xs[n - 1]!) return at(ys, ys.length - 1);
  const i = bisectLeft(xs, x);
  const x0 = xs[i - 1]!;
  const x1 = xs[i]!;
  const y0 = at(ys, i - 1);
  const y1 = at(ys, i);
  if (x1 === x0) return y0;
  return y0 + ((y1 - y0) * (x - x0)) / (x1 - x0);
}

/** Value of the sample nearest to x. xs must be ascending. */
export function nearest(xs: ArrayLike<number>, ys: ArrayLike<number>, x: number): number {
  const n = xs.length;
  if (n === 0) return 0;
  const i = bisectLeft(xs, x);
  if (i <= 0) return at(ys, 0);
  if (i >= n) return at(ys, ys.length - 1);
  return xs[i]! - x < x - xs[i - 1]! ? at(ys, i) : at(ys, i - 1);
}

/** 0, step, 2·step, … and then `total` itself.
 *
 * A lap rarely ends on a whole step, and a grid of whole steps alone stops
 * up to one step short of the line — which is where playback, a finished
 * lap's map dot and the last point of every chart then stopped too. The
 * final interval is shorter than the rest; every consumer reads the grid's
 * own values (or the nearest point to a distance), never index × step.
 */
export function axisGrid(total: number, step: number): number[] {
  const count = Math.trunc(total / step) + 1;
  const grid: number[] = [];
  for (let i = 0; i < count; i++) grid.push(i * step);
  const last = grid.length > 0 ? grid[grid.length - 1]! : 0;
  if (total - last > 0.01) grid.push(roundTo(total, 2));
  return grid;
}

/** The smallest of a non-empty run of numbers, without spreading it onto the
 *  stack. Python's `min()` over the same values. */
export function minOf(values: ArrayLike<number>, lo = 0, hi = values.length): number {
  let best = at(values, lo);
  for (let i = lo + 1; i < hi; i++) {
    const v = values[i]!;
    if (v < best) best = v;
  }
  return best;
}

/** The largest of a non-empty run of numbers; Python's `max()`. */
export function maxOf(values: ArrayLike<number>, lo = 0, hi = values.length): number {
  let best = at(values, lo);
  for (let i = lo + 1; i < hi; i++) {
    const v = values[i]!;
    if (v > best) best = v;
  }
  return best;
}
